import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

const TYPE_OPTIONS = [
  { value: "general", label: "General enquiry" },
  { value: "airline-partnership", label: "Airline partnership" },
  { value: "travel-agent", label: "Travel agent" },
  { value: "corporate", label: "Corporate / Groups" },
  { value: "refund", label: "Refund request" },
  { value: "destination", label: "Destination" },
];

export default function EnquiryForm({
  defaultType = "general",
  title = "Send us an enquiry",
  subtitle = "Our UK team responds within one business day. Operating hours: 9 AM – 11 PM UK time.",
  showTypeSelect = true,
  hiddenContext = {},
  variant = "light",
  testIdPrefix = "enquiry",
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({
    enquiry_type: defaultType,
    full_name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    ...hiddenContext,
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the privacy notice to continue.");
      return;
    }
    setBusy(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const isDark = variant === "dark";
  const labelCls = isDark ? "label-line text-linen/60" : "label-line";
  const inputCls = isDark
    ? "input-line border-linen/30 text-linen placeholder:text-linen/40 focus:border-terracotta"
    : "input-line";

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={`p-10 md:p-14 ${isDark ? "bg-midnight text-linen" : "bg-sand text-ink"}`}
      >
        <div className="overline text-terracotta mb-4">Submitted</div>
        <h3 className="editorial-h3 mb-4">Thank you for getting in touch.</h3>
        <p className="text-sm opacity-80 max-w-md leading-relaxed">
          Your request has been received and will be addressed shortly. A member of the Simba Travel team will respond within one business day. For urgent matters during operating hours please call the relevant department directly.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({
              enquiry_type: defaultType,
              full_name: "",
              email: "",
              phone: "",
              company: "",
              subject: "",
              message: "",
              ...hiddenContext,
            });
          }}
          className={`${isDark ? "btn-secondary border-linen/40 text-linen hover:bg-linen hover:text-midnight" : "btn-secondary"} mt-8`}
          data-testid={`${testIdPrefix}-new`}
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className={`p-8 md:p-12 ${isDark ? "bg-midnight text-linen" : "bg-sand"}`}
    >
      <div className="overline mb-3" style={{ color: isDark ? "#D4A373" : "#B85A3F" }}>
        Get in touch
      </div>
      <h3 className={`editorial-h3 mb-2 ${isDark ? "text-linen" : ""}`}>{title}</h3>
      <p className={`text-sm mb-10 max-w-lg ${isDark ? "text-linen/70" : "text-muted2"}`}>{subtitle}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {showTypeSelect && (
          <div className="md:col-span-2">
            <label className={labelCls}>Enquiry type</label>
            <select
              value={form.enquiry_type}
              onChange={update("enquiry_type")}
              data-testid={`${testIdPrefix}-type`}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-linen text-ink">
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className={labelCls}>Full name *</label>
          <input
            type="text"
            value={form.full_name}
            onChange={update("full_name")}
            required
            data-testid={`${testIdPrefix}-name`}
            className={inputCls}
            placeholder="Jane Mahoso"
          />
        </div>

        <div>
          <label className={labelCls}>Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            required
            data-testid={`${testIdPrefix}-email`}
            className={inputCls}
            placeholder="you@company.co.uk"
          />
        </div>

        <div>
          <label className={labelCls}>Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            data-testid={`${testIdPrefix}-phone`}
            className={inputCls}
            placeholder="+44 20 ..."
          />
        </div>

        <div>
          <label className={labelCls}>Company / Agency</label>
          <input
            type="text"
            value={form.company}
            onChange={update("company")}
            data-testid={`${testIdPrefix}-company`}
            className={inputCls}
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Subject</label>
          <input
            type="text"
            value={form.subject}
            onChange={update("subject")}
            data-testid={`${testIdPrefix}-subject`}
            className={inputCls}
            placeholder="Brief subject line"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Message *</label>
          <textarea
            value={form.message}
            onChange={update("message")}
            required
            rows={4}
            data-testid={`${testIdPrefix}-message`}
            className={`${inputCls} resize-none`}
            placeholder="Tell us about your enquiry..."
          />
        </div>
      </div>

      <label className="flex items-start gap-3 mt-8 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid={`${testIdPrefix}-consent`}
          className="mt-1 accent-terracotta"
        />
        <span className={`text-xs leading-relaxed ${isDark ? "text-linen/70" : "text-muted2"}`}>
          I have read the{" "}
          <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-terracotta">Privacy Notice</a>
          {" "}and agree that Simba Travel may use the information I provide to respond to my enquiry, in line with UK GDPR.
        </span>
      </label>

      <button
        type="submit"
        disabled={busy}
        data-testid={`${testIdPrefix}-submit`}
        className={`${isDark ? "btn-accent" : "btn-primary"} mt-10 disabled:opacity-60`}
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            Submit enquiry <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
